import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import styled from "@emotion/styled";
import {
  Alert as MuiAlert,
  Box,
  MenuItem,
  TextField as MuiTextField,
  ListItemText,
  Button,
  Stack,
  Paper,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CircularProgress from "@mui/material/CircularProgress";
import { spacing } from "@mui/system";
import useNotification from "hooks/useNotification";
import useReferentiel from "hooks/useReferentiel";
import UploadSingleFile from "components/file/UploadSingleFile";
import ImgBoxFromUrl from "components/media/ImgBoxFromUrl";
import AudioBoxFromUrl from "components/media/AudioBoxFromUrl";
import EditFormSubtitle from "components/media/EditFormSubtitle";
import { API_URL } from "config";
import { init } from "i18next";
// ----------------------------------------------------------------------
const TextField = styled(MuiTextField)(spacing);
const Item = styled(Paper)(({ theme }) => ({
  //backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));
export default function RefsubjectForm({ editData, onComplete }) {
  const { sendFormdata, saveRefsubject, initRefs } = useReferentiel();
  const { notif } = useNotification();
  const { appconfigs, refs } = useReferentiel();

  const dataSchema = Yup.object().shape({
    code: Yup.string().max(20).required("Veuillez renseigner le code"),
    label: Yup.string().max(100).required(`Veuillez renseigner le libellé`),
    reftheme: Yup.string().max(100).required(`Veuillez renseigner le thème`),
    bgimage:
      editData && editData.id
        ? Yup.string()
        : Yup.mixed().required("Veuillez renseigner l'image de fond"),
    image:
      editData && editData.id
        ? Yup.string()
        : Yup.mixed().required("Veuillez renseigner l'image"),
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: editData && editData.code ? editData.code : "",
      label: editData && editData.label ? editData.label : "",
      reftheme: editData && editData.reftheme ? editData.reftheme.id : "",
    },
    validationSchema: dataSchema,
    onSubmit: async () => {
      try {
        const newData =
          editData && editData.id ? { ...editData, ...values } : { ...values };
        /* in case file upload is choiced we need to make formdata */
        const isFormdata =
          values.bgimage ||
          values.image ||
          values.femalevoice ||
          values.malevoice;
        if (isFormdata) {
          const formData = new FormData();
          delete newData.modifiedby;
          delete newData.createdby;
          if (values.bgimage) {
            delete newData.bgimage;
            formData.append("bgimage", values.bgimage);
          }
          if (values.image) {
            delete newData.image;
            formData.append("image", values.image);
          }
          if (values.femalevoice) {
            delete newData.femalevoice;
            formData.append("femalevoice", values.femalevoice);
          }
          if (values.malevoice) {
            delete newData.malevoice;
            formData.append("malevoice", values.malevoice);
          }
          Object.entries(newData).forEach(([e, k]) => {
            formData.append(e, k);
          });

          await sendFormdata(newData, formData);
        } else {
          await saveRefsubject({
            ...newData,
          });
        }

        await initRefs();
        notif("Sauvegarde réussie");
        onComplete();
      } catch (error) {
        console.error(error);
        notif(null, error);
        setSubmitting(false);
      }
    },
  });
  const handleCompleteTranscript = async () => {
    await initRefs();
  };
  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    handleChange,
    setFieldValue,
  } = formik;

  if (!refs) return;
  const { reftheme } = refs;
  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="text"
            label="Code"
            {...getFieldProps("code")}
            error={Boolean(touched.code && errors.code)}
            helperText={touched.code && errors.code}
            my={2}
          />
          <TextField
            fullWidth
            type="text"
            name="label"
            label="Libellé"
            {...getFieldProps("label")}
            error={Boolean(touched.label && errors.label)}
            helperText={touched.label && errors.label}
            my={2}
          />

          <TextField
            fullWidth
            select
            label="Thème"
            value={values.reftheme}
            onChange={handleChange("reftheme")}
            {...getFieldProps("reftheme")}
            error={Boolean(touched.reftheme && errors.reftheme)}
            helperText={touched.reftheme && errors.reftheme}
            my={2}
          >
            {reftheme.map((option, i) => (
              <MenuItem key={i} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ my: 2 }}
            direction={{ sm: "column", md: "row" }}
            spacing={{ xs: 1, sm: 2, md: 4 }}
          >
            {editData && editData.bgimage && (
              <Item>
                <ImgBoxFromUrl
                  image={`${API_URL.getstream}/refsubject_${editData.id}/${editData.bgimage}`}
                  sx={{
                    borderRadius: 1,
                    objectFit: "cover",
                    width: "150px",
                    height: "auto",
                  }}
                />
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.bgimage}
                label="Image de fond"
                onChange={(val) => setFieldValue("bgimage", val)}
                caption="(Les formats *.jpeg and *.png sont acceptés)"
                error={Boolean(touched.bgimage && errors.bgimage)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "image/jpeg": [".jpeg", ".png"],
                  },
                }}
                mediaType="image"
              />
            </Item>
            {editData && editData.image && (
              <Item>
                <ImgBoxFromUrl
                  image={`${API_URL.getstream}/refsubject_${editData.id}/${editData.image}`}
                  sx={{
                    borderRadius: 1,
                    objectFit: "cover",
                    width: "150px",
                    height: "auto",
                  }}
                />
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.image}
                label="Image d'illustration"
                onChange={(val) => setFieldValue("image", val)}
                caption="(Les formats *.jpeg and *.png sont acceptés)"
                error={Boolean(touched.image && errors.image)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "image/jpeg": [".jpeg", ".png"],
                  },
                }}
                mediaType="image"
              />
            </Item>
          </Stack>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ my: 2 }}
          >
            {editData && editData.femalevoice && (
              <Item>
                <AudioBoxFromUrl
                  audio={`${API_URL.getstream}/refsubject_${editData.id}/${editData.femalevoice}`}
                />
                {editData.femaletranscripts && (
                  <EditFormSubtitle
                    transcripts={editData.femaletranscripts}
                    model="refsubject"
                    field="femaletranscripts"
                    idModel={editData.id}
                    onComplete={handleCompleteTranscript}
                  />
                )}
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.femalevoice}
                label="Voix féminine"
                onChange={(val) => setFieldValue("femalevoice", val)}
                caption="(Les formats *.mp3 sont acceptés)"
                error={Boolean(touched.femalevoice && errors.femalevoice)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "audio/mp3": [".mp3"],
                  },
                }}
                mediaType="audio"
              />
            </Item>
          </Stack>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ my: 2 }}
          >
            {editData && editData.malevoice && (
              <Item>
                <AudioBoxFromUrl
                  audio={`${API_URL.getstream}/refsubject_${editData.id}/${editData.malevoice}`}
                />
                {editData.maletranscripts && (
                  <EditFormSubtitle
                    transcripts={editData.maletranscripts}
                    model="refsubject"
                    field="maletranscripts"
                    idModel={editData.id}
                    onComplete={handleCompleteTranscript}
                  />
                )}
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.malevoice}
                label="Voix masculine"
                onChange={(val) => setFieldValue("malevoice", val)}
                caption="(Les formats *.mp3 sont acceptés)"
                error={Boolean(touched.malevoice && errors.malevoice)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "audio/mp3": [".mp3"],
                  },
                }}
                mediaType="audio"
              />
            </Item>
          </Stack>
          <Button
            sx={{ my: 2 }}
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            endIcon={
              isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                <DoneIcon size={20} />
              )
            }
          >
            Valider
          </Button>
        </Form>
      </FormikProvider>
    </>
  );
}
